
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid"; 
import { getPlatformType } from "@/utils/platform-utils";
import { AppTrackingService } from './app-tracking-transparency';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';

// Event names according to Meta's standard event names
export enum MetaEventName {
  COMPLETE_REGISTRATION = 'CompleteRegistration',
  VIEW_CONTENT = 'ViewContent',
  INITIATE_CHECKOUT = 'InitiateCheckout',
  ADD_TO_CART = 'AddToCart',
  PURCHASE = 'Purchase',
  SUBSCRIBE = 'Subscribe'
}

// Types for Meta events
interface MetaUserData {
  email?: string;
  clientIp?: string;
  userAgent?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  externalId?: string;
}

interface MetaCustomData {
  [key: string]: string | number | boolean;
}

interface MetaEventOptions {
  eventId?: string;
  sourceUrl?: string;
  userData: MetaUserData;
  customData?: MetaCustomData;
}

/**
 * Generate device extinfo in Meta's "a2" format for mobile apps
 * Format: [extinfo_version, bundle_id, app_version, long_version, device_name, device_version, width, height, density, cpu_cores, storage_mb, free_storage_mb, device_timezone]
 */
async function getDeviceExtInfo(): Promise<string | null> {
  try {
    const platformType = getPlatformType();
    if (platformType === 'web') {
      return null; // Web doesn't need extinfo
    }

    const [deviceInfo, appInfo] = await Promise.all([
      Device.getInfo(),
      App.getInfo()
    ]);

    // Generate extinfo array in "a2" format
    const extinfo = [
      "a2", // extinfo version
      appInfo.id || "app.pulsefiction.pulse", // bundle_id
      appInfo.version || "1.0.0", // app_version  
      appInfo.build || "1", // long_version (build number)
      deviceInfo.model || "Unknown", // device_name
      deviceInfo.osVersion || "Unknown", // device_version
      typeof window !== 'undefined' ? window.screen.width || 0 : 0, // width
      typeof window !== 'undefined' ? window.screen.height || 0 : 0, // height
      typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, // density
      "Unknown", // cpu_cores (not available via Capacitor)
      "Unknown", // storage_mb (not available via Capacitor)
      "Unknown", // free_storage_mb (not available via Capacitor)
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Unknown" // device_timezone
    ];

    return JSON.stringify(extinfo);
  } catch (error) {
    console.warn('Failed to generate device extinfo:', error);
    return null;
  }
}

// Get cookies for Facebook tracking
function getFacebookCookies(): { fbc?: string; fbp?: string } {
  const cookies = document.cookie.split(';');
  const result: { fbc?: string; fbp?: string } = {};
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === '_fbc') {
      result.fbc = value;
    } else if (name === '_fbp') {
      result.fbp = value;
    }
  }
  
  return result;
}

/**
 * Validates user data to ensure proper formats are sent to Meta API
 */
function validateUserData(userData: MetaUserData): MetaUserData {
  const validated: MetaUserData = {};
  
  // Ensure all fields are strings if present
  if (userData.email) {
    validated.email = String(userData.email).trim();
  }
  
  if (userData.externalId) {
    validated.externalId = String(userData.externalId).trim();
  }
  
  if (userData.firstName) {
    validated.firstName = String(userData.firstName).trim();
  }
  
  if (userData.lastName) {
    validated.lastName = String(userData.lastName).trim();
  }
  
  if (userData.phone) {
    validated.phone = String(userData.phone).trim();
  }
  
  if (userData.clientIp) {
    validated.clientIp = String(userData.clientIp).trim();
  }
  
  if (userData.userAgent) {
    validated.userAgent = String(userData.userAgent).trim();
  }
  
  return validated;
}

/**
 * Send an event to Meta Conversions API via Edge Function
 */
export const sendMetaEvent = async (
  eventName: MetaEventName, 
  options: MetaEventOptions
): Promise<boolean> => {
  try {
    // 🎯 Check ATT permission before sending to Meta
    if (!AppTrackingService.hasTrackingPermission()) {
      console.log('No tracking permission, skipping Meta event:', eventName);
      return false;
    }

    // Determine if we're in a native app or web environment
    const platformType = getPlatformType();
    const actionSource = platformType === 'web' ? 'website' : 'app';
    
    // Generate unique event ID if not provided
    const eventId = options.eventId || uuidv4();
    
    // Get user agent from browser if not provided
    const userAgent = options.userData.userAgent || navigator.userAgent;
    
    // Get current URL if source URL not provided
    const sourceUrl = options.sourceUrl || window.location.href;

    // Enhance user data with Facebook cookies if available
    const fbCookies = getFacebookCookies();
    
    // Get current user info
    const { data: userData } = await supabase.auth.getUser();
    // Extract the user ID if available
    const userId = userData?.user?.id || null;
    const userEmail = options.userData.email || userData?.user?.email || null;
    
    // Create a validated copy of user data to ensure proper types
    const validatedUserData = validateUserData({
      ...options.userData,
      email: userEmail,
      userAgent,
      // Add user ID as external ID directly (not hashed)
      externalId: userId || options.userData.externalId
    });

    // Validate required fields
    if (!validatedUserData.email && !validatedUserData.externalId) {
      console.warn('Meta event is missing both email and user ID identifiers');
    }
    
    // Log the data we're sending for debugging
    console.log('Sending Meta event:', eventName);
    console.log('User data being sent:', {
      email: validatedUserData.email ? `✓ present (${validatedUserData.email})` : "✗ missing",
      externalId: validatedUserData.externalId ? `✓ present (${validatedUserData.externalId})` : "✗ missing",
      userAgent: validatedUserData.userAgent ? "✓ present" : "✗ missing",
    });
    
    // Extra debug logs to verify data format before sending
    console.log('Email type:', typeof validatedUserData.email);
    console.log('ExternalId type:', typeof validatedUserData.externalId);

    // 🆕 Get ATT permission status for mobile app events
    const advertiserTrackingEnabled = actionSource === 'app' ? 
      AppTrackingService.hasTrackingPermission() : undefined;

    console.log('ATT permission status:', advertiserTrackingEnabled);

    // 🆕 Get device extinfo for mobile app events
    const extinfo = actionSource === 'app' ? await getDeviceExtInfo() : null;
    console.log('Generated extinfo:', extinfo ? '✓ available' : '✗ not available');

    // Call the Edge Function to send the event to Meta
    const { data, error } = await supabase.functions.invoke('meta-event-tracking', {
      body: {
        eventName,
        eventId,
        sourceUrl,
        userData: validatedUserData,
        customData: options.customData || {},
        actionSource,
        advertiserTrackingEnabled, // 🆕 Pass ATT status to edge function
        extinfo // 🆕 Pass device extinfo for mobile events
      }
    });

    if (error) {
      console.error('Error sending Meta event:', error);
      return false;
    }

    // Log success in development mode
    if (import.meta.env.DEV) {
      console.log(`Meta ${eventName} event sent successfully:`, data);
    }

    return true;
  } catch (error) {
    console.error(`Failed to send Meta ${eventName} event:`, error);
    return false;
  }
};

/**
 * Track user registration completion
 */
export const trackRegistrationComplete = async (email: string, firstName?: string, lastName?: string): Promise<void> => {
  await sendMetaEvent(MetaEventName.COMPLETE_REGISTRATION, {
    sourceUrl: 'https://pulsefiction.app/signup',
    userData: { 
      email,
      firstName,
      lastName,
      userAgent: navigator.userAgent
    }
  });
};

/**
 * Track chapter content view (after 5 seconds)
 */
export const trackChapterView = async (storyTitle: string, email?: string, userId?: string): Promise<void> => {
  try {
    // If no email/userId provided, try to get from current auth
    if (!email && !userId) {
      const { data: userData } = await supabase.auth.getUser();
      email = userData?.user?.email || undefined;
      userId = userData?.user?.id || undefined;
    }

    await sendMetaEvent(MetaEventName.VIEW_CONTENT, {
      eventId: uuidv4(), // Always generate a unique ID for view events
      sourceUrl: window.location.href,
      userData: { 
        email: email || "",
        userAgent: navigator.userAgent,
        externalId: userId || ""
      },
      customData: {
        content_name: storyTitle,
        content_category: 'chapter',
        content_type: 'text',
        content_ids: window.location.pathname.split('/').pop() // Single string, not an array
      }
    });
    console.log("Chapter view tracked successfully:", storyTitle);
  } catch (error) {
    console.error('Error tracking chapter view:', error);
  }
};

/**
 * Track checkout initiation
 */
export const trackCheckoutInitiation = async (
  email: string, 
  planPrice: number, 
  currency: string = 'BRL',
  planName?: string,
  userId?: string
): Promise<void> => {
  // If no userId provided, try to get from current auth
  if (!userId) {
    const { data: userData } = await supabase.auth.getUser();
    userId = userData?.user?.id || undefined;
  }

  // Make sure email and userId are valid strings before sending
  await sendMetaEvent(MetaEventName.INITIATE_CHECKOUT, {
    sourceUrl: 'https://pulsefiction.app/plans',
    userData: { 
      email: email ? String(email).trim() : "",
      userAgent: navigator.userAgent,
      externalId: userId ? String(userId).trim() : ""
    },
    customData: {
      value: planPrice,
      currency,
      content_category: 'subscription',
      content_name: planName || 'Premium Plan'
    }
  });
};

/**
 * Track purchase completion
 */
export const trackPurchase = async (
  email: string,
  amount: number,
  currency: string = 'BRL',
  subscriptionId?: string,
  userId?: string
): Promise<void> => {
  // If no userId provided, try to get from current auth
  if (!userId) {
    const { data: userData } = await supabase.auth.getUser();
    userId = userData?.user?.id || undefined;
  }

  await sendMetaEvent(MetaEventName.PURCHASE, {
    sourceUrl: window.location.href,
    userData: {
      email,
      userAgent: navigator.userAgent,
      externalId: userId
    },
    customData: {
      value: amount,
      currency,
      content_category: 'subscription',
      content_name: 'Premium Subscription',
      content_ids: subscriptionId || undefined, // Single string or undefined, not an array
      num_items: 1
    }
  });
};
